import {
  ActionIcon,
  Alert,
  Box,
  Button,
  Center,
  CloseButton,
  Container,
  Flex,
  Group,
  Paper,
  Progress,
  rem,
  ScrollArea,
  Select,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconFileTypeXml,
  IconPlus,
  IconSearch,
  IconTrash,
  IconUpload,
} from '@tabler/icons-react';
import { FixAppHeight } from 'components/FixAppHeight';
import { PageTitle } from 'components/PageTitle';
import { useCompanyConfiguration } from 'init/use-company-configuration';
import { useAtom } from 'jotai';
import { i18nKeys, useTranslation } from 'locales';
import { useEffect, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useHistory } from 'react-router';
import { isDefined, isNullish } from 'remeda';
import { useLoadRecoveryPlans } from 'modules/Settings/Workflows/api';
import HTML from 'shared/components/HTML/HTML';
import { accountActions } from 'store/account/account.actions';

import { ublProgressAtom } from '../ublProgressAtom';

const ACCEPTED_EXTENSION = '.xml';
const ROW_HEIGHT = 60;
const ROW_GAP = 8;
const ROW_PITCH = ROW_HEIGHT + ROW_GAP;
const OVERSCAN = 8;

export const InvoiceUblImport = () => {
  const { t } = useTranslation();
  const history = useHistory();

  const company = useCompanyConfiguration();
  const hasMultiplePlans = company.active_plans_count > 1;

  const [files, setFiles] = useState<Array<File>>([]);
  const [planId, setPlanId] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [progress, setProgress] = useAtom(ublProgressAtom);

  const { recoveryPlans } = useLoadRecoveryPlans({ enabled: hasMultiplePlans });
  const planOptions = (recoveryPlans ?? []).map(plan => ({
    value: plan.id,
    label: plan.name,
  }));

  const isImporting = progress.active && isNullish(progress.errors);
  const importErrors: Array<string> = Array.isArray(progress.errors) ? progress.errors : [];
  const hasErrors = importErrors.length > 0;

  const normalizedFilter = filter.trim().toLowerCase();
  const visibleFiles = files
    .map((file, index) => ({ file, index }))
    .filter(({ file }) => file.name.toLowerCase().includes(normalizedFilter));

  const viewportRef = useRef<HTMLDivElement>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(0);
  const hasFiles = files.length > 0;

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!hasFiles || !viewport) return;
    const measure = () => setViewportHeight(viewport.clientHeight);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(viewport);
    return () => observer.disconnect();
  }, [hasFiles]);

  const totalHeight = visibleFiles.length * ROW_PITCH;
  const firstVisibleIndex = Math.max(0, Math.floor(scrollTop / ROW_PITCH) - OVERSCAN);
  const lastVisibleIndex = Math.min(
    visibleFiles.length,
    Math.ceil((scrollTop + viewportHeight) / ROW_PITCH) + OVERSCAN,
  );
  const virtualFiles = visibleFiles
    .slice(firstVisibleIndex, lastVisibleIndex)
    .map((entry, offset) => ({ ...entry, top: (firstVisibleIndex + offset) * ROW_PITCH }));

  const addFiles = (accepted: Array<File>) => {
    setProgress({ active: false });
    setFiles(current => {
      const existingKeys = new Set(current.map(file => `${file.name}:${file.size}`));
      const additions = accepted.filter(file => !existingKeys.has(`${file.name}:${file.size}`));
      return [...current, ...additions];
    });
  };

  const removeFile = (index: number) => {
    setFiles(current => current.filter((_, fileIndex) => fileIndex !== index));
  };

  const updateFilter = (value: string) => {
    setFilter(value);
    viewportRef.current?.scrollTo({ top: 0 });
    setScrollTop(0);
  };

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    accept: ACCEPTED_EXTENSION,
    multiple: true,
    noClick: true,
    noKeyboard: true,
    onDropAccepted: addFiles,
  });

  const hasAutoOpened = useRef(false);
  const isImportActiveOnMount = useRef(progress.active).current;
  useEffect(() => {
    if (isImportActiveOnMount) return;
    setProgress({ active: false });
    if (!hasAutoOpened.current) {
      hasAutoOpened.current = true;
      open();
    }
  }, [open, setProgress, isImportActiveOnMount]);

  const isMountedRef = useRef(true);
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const canImport = files.length > 0 && !isImporting && (!hasMultiplePlans || isDefined(planId));

  const startImport = () => {
    setProgress({ active: true, value: 0 });
    accountActions.importInvoice({
      ublFiles: files,
      planId: planId ?? undefined,
      callback: () => {
        setProgress({ active: false });
        if (isMountedRef.current) {
          setFiles([]);
          setFilter('');
          history.push('/invoices/to-confirm');
        }
      },
    });
  };

  return (
    <Flex direction="column" h="100%">
      <FixAppHeight pb={20} />

      <PageTitle>
        <PageTitle.Back />
        {t(i18nKeys.INVOICING.IMPORT.UBL_INVOICES)}
        <PageTitle.Actions>
          <Button
            leftSection={<IconUpload size={16} stroke={1.5} />}
            disabled={!canImport}
            loading={isImporting}
            onClick={startImport}
          >
            {files.length > 0 ? `${t(i18nKeys.IMPORT)} (${files.length})` : t(i18nKeys.IMPORT)}
          </Button>
        </PageTitle.Actions>
      </PageTitle>

      <input {...getInputProps()} />

      <Container
        size="sm"
        px={0}
        w="100%"
        style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
      >
        {files.length === 0 ? (
          <Paper
            {...getRootProps()}
            onClick={open}
            withBorder
            radius="md"
            p="xl"
            mt="lg"
            style={theme => ({
              cursor: 'pointer',
              borderStyle: 'dashed',
              borderWidth: rem(2),
              borderColor: isDragActive ? theme.colors.purple[5] : theme.colors.gray[4],
              backgroundColor: isDragActive ? theme.colors.purple[0] : undefined,
              transition: 'background-color 150ms ease, border-color 150ms ease',
            })}
          >
            <Center mih={rem(180)}>
              <Stack align="center" gap="xs">
                <ThemeIcon size={rem(64)} radius="md" variant="light" color="purple">
                  <IconFileTypeXml size={32} stroke={1.5} />
                </ThemeIcon>
                <Text fw={600} size="lg" ta="center">
                  {t(i18nKeys.INVOICING.IMPORT.UBL_UPLOAD.DROP_TITLE)}
                </Text>
                <Text c="dimmed" size="sm" ta="center">
                  {t(i18nKeys.INVOICING.IMPORT.UBL_UPLOAD.DROP_SUBTITLE)}
                </Text>
              </Stack>
            </Center>
          </Paper>
        ) : (
          <Flex direction="column" gap="md" pt="lg" style={{ flex: 1, minHeight: 0 }}>
            <Text c="dimmed" size="sm">
              {t(i18nKeys.INVOICING.IMPORT.UBL_UPLOAD.SUBTITLE)}
            </Text>

            {hasMultiplePlans && (
              <Select
                label={t(i18nKeys.RECOVERY_PLAN)}
                placeholder={t(i18nKeys.PLAN.SELECT_PLAN)}
                data={planOptions}
                value={planId}
                onChange={setPlanId}
                disabled={isImporting}
                searchable
              />
            )}

            <Group justify="space-between" align="center">
              <Text fw={600}>
                {t(i18nKeys.INVOICING.IMPORT.UBL_UPLOAD.FILES_SELECTED)} ({files.length})
              </Text>
              <Button
                variant="subtle"
                size="compact-sm"
                leftSection={<IconPlus size={16} stroke={1.5} />}
                onClick={open}
                disabled={isImporting}
              >
                {t(i18nKeys.INVOICING.IMPORT.UBL_UPLOAD.ADD_MORE)}
              </Button>
            </Group>

            {files.length > 1 && (
              <TextInput
                placeholder={t(i18nKeys.INVOICING.IMPORT.UBL_UPLOAD.FILTER_PLACEHOLDER)}
                leftSection={<IconSearch size={16} stroke={1.5} />}
                value={filter}
                onChange={event => updateFilter(event.currentTarget.value)}
                rightSection={
                  filter ? (
                    <CloseButton size="sm" onClick={() => updateFilter('')} aria-label="clear" />
                  ) : null
                }
              />
            )}

            {isImporting && (
              <Stack gap={rem(6)}>
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">
                    {t(i18nKeys.INVOICING.IMPORT.UBL_UPLOAD.IMPORTING)}
                  </Text>
                  <Text size="sm" fw={600}>
                    {Math.round(progress.value ?? 0)}%
                  </Text>
                </Group>
                <Progress value={progress.value ?? 0} size="lg" radius="xl" striped animated />
              </Stack>
            )}

            {visibleFiles.length === 0 ? (
              <Text c="dimmed" size="sm" ta="center" py="md">
                {t(i18nKeys.INVOICING.IMPORT.UBL_UPLOAD.NO_MATCH)}
              </Text>
            ) : (
              <ScrollArea
                viewportRef={viewportRef}
                onScrollPositionChange={position => setScrollTop(position.y)}
                type="auto"
                offsetScrollbars
                style={{ flex: 1, minHeight: 0 }}
              >
                <Box style={{ position: 'relative', height: totalHeight }}>
                  {virtualFiles.map(({ file, index, top }) => (
                    <Box
                      key={`${file.name}:${file.size}`}
                      style={{ position: 'absolute', top, left: 0, right: 0, height: ROW_HEIGHT }}
                    >
                      <Paper withBorder radius="md" px="sm" h={ROW_HEIGHT}>
                        <Group justify="space-between" wrap="nowrap" h="100%">
                          <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
                            <ThemeIcon variant="light" color="purple" radius="md" size="lg">
                              <IconFileTypeXml size={18} stroke={1.5} />
                            </ThemeIcon>
                            <Box style={{ minWidth: 0 }}>
                              <Text size="sm" fw={500} truncate>
                                {file.name}
                              </Text>
                              <Text size="xs" c="dimmed">
                                {formatBytes(file.size)}
                              </Text>
                            </Box>
                          </Group>
                          <ActionIcon
                            variant="subtle"
                            color="gray"
                            onClick={() => removeFile(index)}
                            disabled={isImporting}
                            aria-label={t(i18nKeys.DELETE)}
                          >
                            <IconTrash size={16} stroke={1.5} />
                          </ActionIcon>
                        </Group>
                      </Paper>
                    </Box>
                  ))}
                </Box>
              </ScrollArea>
            )}

            {hasErrors && (
              <Alert
                variant="light"
                color="red"
                radius="md"
                icon={<IconAlertTriangle size={18} stroke={1.5} />}
                title={t(i18nKeys.ERROR.IMPORT_ERROR)}
              >
                <ScrollArea.Autosize mah={rem(160)}>
                  <Stack gap={rem(4)}>
                    {importErrors.map(message => (
                      <HTML key={message} html={message} />
                    ))}
                  </Stack>
                </ScrollArea.Autosize>
              </Alert>
            )}
          </Flex>
        )}
      </Container>
    </Flex>
  );
};

const formatBytes = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  const kilobytes = bytes / 1024;
  if (kilobytes < 1024) return `${Math.round(kilobytes)} KB`;
  return `${(kilobytes / 1024).toFixed(1)} MB`;
};
